'use client';
import React, {
  ChangeEvent,
  FunctionComponent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { getSession, useSession } from 'next-auth/react';
import useUser from 'hooks/useUser';
import ProfileForm from 'components/Forms/ProfileForm';
import '/public/css/pages/story.css';
import '/public/css/pages/profile.css';

interface ProfileInfo {
  id?: string | null;
  name?: string | null;
  email?: string | null;
  profileImage?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  preferredUsername?: string | null;
  userName?: string | null;
}

const UserPage: FunctionComponent = () => {
  const sessionData = useSession();
  const session = sessionData?.data;
  const { data, error, isValidating } = useUser();

  async function testSession() {
    const result = await getSession(); // calling getSession calls the jwt callback, which will update the accessToken if required
    console.log('session', result);
  }

  const [uploadedImage, setUploadedImage] = useState<string>('');
  const [isEditActive, setIsEditActive] = useState(false);
  const [formData, setFormData] = useState<ProfileInfo>({
    id: '',
    firstName: '',
    lastName: '',
    preferredUsername: '',
    profileImage: '',
    userName: '',
    email: '',
  });
  const [apiMessage, setApiMessage] = useState<string>('');

  function updateFormData() {
    setFormData({
      id: (session?.user as any).id || '',
      firstName: session?.user.given_name || '',
      lastName: session?.user.family_name || '',
      preferredUsername: session?.user.preferred_username || '',
      profileImage: session?.user.image || '',
      userName: session?.user.username || '',
      email: (session?.user as any).email || '',
    });
  }

  useEffect(() => {
    if (session?.user) {
      updateFormData();
    }
  }, [session]);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  const convertImageToData = async (file: File) => {
    const img = new Image();
    if (typeof file === 'object') {
      img.src = URL.createObjectURL(file);
    }
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const maxSize = 200;

      const scaleSize = Math.min(maxSize / img.width, maxSize / img.height);
      canvas.width = img.width * scaleSize;
      canvas.height = img.height * scaleSize;

      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        const dataUrl = canvas.toDataURL('image/png');
        const base64String = dataUrl.split(',')[1];
        console.log({ base64String });
        setUploadedImage(base64String);
      } else {
        console.error('Failed to get canvas context');
      }
    };
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target?.files && e.target.files[0]) {
      const file = e.target.files[0];
      console.log({ file });
      convertImageToData(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    let result: any;
    try {
      const response = await fetch(
        `${process.env.NEXTAUTH_URL || ''}/api/auth/update-profile`,
        {
          method: 'PUT',
          credentials: 'same-origin',
          cache: 'no-cache',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...formData,
            profileImage: uploadedImage,
          }),
        }
      );

      if (response.status === 400) {
        result = await response.json();
        return setApiMessage(result.message || '');
      }

      result = await response.json();
      await sessionData.update({
        ...result.data.profile,
        profileImage: result.data.profile.profileImage || session?.user.image,
      });
      toggleEdit();
    } catch (error) {
      console.error('Error updating profile:', error);
    }
  };

  const toggleEdit = () => {
    setIsEditActive(!isEditActive);
  };

  return (
    <div className="story-content container profile-container">
      {error && (
        <div>
          An error occurred.<pre>{JSON.stringify(error, null, 2)}</pre>
        </div>
      )}

      {isValidating && !data && <div>Loading...</div>}
      {/* <button onClick={testSession}>Test Session</button> */}
      <ProfileForm
        handleFileChange={handleFileChange}
        handleInputChange={handleInputChange}
        handleSubmit={handleSubmit}
        formData={formData}
        fileInputRef={fileInputRef}
        apiMessage={apiMessage}
      />
    </div>
  );
};

export default UserPage;
